import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { useSimulator } from "../Simulator/SimulatorStore";

Chart.register(...registerables);

export default function PoliciesByDayChart() {
  const { policiesPerDay, global } = useSimulator();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  // Keep a rolling 30-day history so bars change individually.
  const historyRef = useRef<number[]>([]);

  const inputsZero =
    global.dailyLeadVolume === 0 &&
    global.leadQuality === 0 &&
    global.baseConversionRate === 0 &&
    global.cycleTimeImpact === 0;

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    const days = 30;
    const labels = Array.from({ length: days }, (_, i) => `${i + 1}`);

    if (!inputsZero && policiesPerDay > 0) {
      if (historyRef.current.length === 0) {
        historyRef.current = Array.from({ length: days }, () => {
          const factor = 0.85 + Math.random() * 0.3; // ±15% variation
          return Math.max(0, Math.round(policiesPerDay * factor));
        });
      } else {
        const factor = 0.85 + Math.random() * 0.3;
        const nextVal = Math.max(0, Math.round(policiesPerDay * factor));
        historyRef.current = [...historyRef.current.slice(1), nextVal];
      }
    }

    const values = inputsZero
      ? Array.from({ length: days }, () => 0)
      : historyRef.current.length > 0
        ? historyRef.current
        : Array.from({ length: days }, () => 0);

    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, "#34D399");
    gradient.addColorStop(1, "#059669");

    if (!chartRef.current) {
      chartRef.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Policies Written",
              data: values,
              backgroundColor: values.map(() => gradient), // per-bar keeps gradient look
              borderColor: "#047857",
              borderWidth: 1,
              borderRadius: 4,
              hoverBorderColor: "#0ea5e9",
              barPercentage: 0.95,
              categoryPercentage: 0.95,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: { color: "#666", font: { size: 10 } },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              suggestedMax: Math.max(8, ...values) + 2,
              ticks: { stepSize: 2, color: "#666", font: { size: 11 } },
              grid: { color: "#e5e7eb" },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (items) => `Day ${items[0].label}`,
                label: (ctx) => `Policies Written: ${ctx.parsed.y}`,
              },
            },
          },
        },
      });
    } else {
      chartRef.current.data.labels = labels;
      chartRef.current.data.datasets[0].data = values as any;
      chartRef.current.data.datasets[0].backgroundColor = values.map(() => gradient);
      chartRef.current.options.scales!.y!.suggestedMax = Math.max(8, ...values) + 2;
      chartRef.current.update();
    }

  }, [policiesPerDay, inputsZero]);

  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Policies Written by Day</h4>
      <div className="relative" style={{ height: 300 }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}
